import React, { useState, useContext, useEffect, useRef } from "react";
import { api } from "../../services/baseUrl";
import { ThemeContext } from "../../hooks/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";

const Register = () => {
    const [formData, setFormData] = useState({
        nickname: "",
        firstname: "",
        lastname: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: "user",
        siret: "",
        stripeEmail: "",
        bankAccountName: "",
        fullAddress: "",
        motivation: "",
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const location = useLocation();
    const navigate = useNavigate();
    const creatorRef = useRef(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (formData.password !== formData.confirmPassword) {
            setError("Les mots de passe ne correspondent pas !");
            setSuccess("");
            return;
        }

        if (formData.role === "creator") {
            // Validation SIRET
            if (!/^\d{14}$/.test(formData.siret)) {
                setError("Numéro de SIRET invalide (14 chiffres).");
                setSuccess("");
                return;
            }
            if (!formData.stripeEmail || !formData.bankAccountName || !formData.fullAddress) {
                setError("Tous les champs créateur sont obligatoires.");
                setSuccess("");
                return;
            }
        }

        try {
            const payload = {
                nickname: formData.nickname,
                firstname: formData.firstname,
                lastname: formData.lastname,
                email: formData.email,
                password: formData.password,
                password_confirmation: formData.confirmPassword,
                role: formData.role,
            };
            if (formData.role === "creator") {
                payload.siret = formData.siret;
                payload.stripe_email = formData.stripeEmail;
                payload.bank_account_name = formData.bankAccountName;
                payload.full_address = formData.fullAddress;
                payload.motivation = formData.motivation;
            }
            const response = await api.post("/register", payload);
            console.log(response.data);
            const onboardingUrl = response?.data?.data?.onboarding_url;

            if (formData.role === "creator" && onboardingUrl) {
                setSuccess("Compte créateur créé. Redirection vers Stripe pour finaliser le reversement 95% / 5%.");
                document.location.assign(onboardingUrl);
                return;
            }

            setSuccess("Inscription réussie. Vous pouvez maintenant vous connecter.");
            setFormData({
                nickname: "",
                firstname: "",
                lastname: "",
                email: "",
                password: "",
                confirmPassword: "",
                role: "user",
                siret: "",
                stripeEmail: "",
                bankAccountName: "",
                fullAddress: "",
                motivation: "",
            });
            setTimeout(() => navigate("/login"), 800);
        } catch (error) {
            console.error(error);
            setError(error.response?.data?.message || "Erreur lors de l'inscription. Vérifiez vos informations.");
            setSuccess("");
        }
    };

    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const roleParam = params.get("role");

        if (roleParam === "creator" || roleParam === "user") {
            setFormData((prev) => ({ ...prev, role: roleParam }));
        }

        if (location.hash === "#creator" && creatorRef.current) {
            creatorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [location.search, location.hash]);

    return (
        <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#0f1114]' : 'bg-slate-50'} px-4`}>
            <div className={`${isDark ? 'bg-[#0b0b0c] text-white' : 'bg-white/95 text-gray-800'} p-8 rounded-lg shadow-lg max-w-md w-full`}>
                <h2 className="text-2xl font-bold text-center mb-6">Inscription</h2>

                {success && <p className="text-green-600 text-center mb-4">{success}</p>}
                {error && <p className="text-red-500 text-center mb-4">{error}</p>}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <p className={`text-sm font-semibold mb-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            Choisissez votre profil
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, role: "user" })}
                                className={`rounded-lg border p-4 text-left transition ${
                                    formData.role === "user"
                                        ? (isDark ? 'border-rose-500 bg-rose-500/10' : 'border-rose-500 bg-rose-50')
                                        : (isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-300')
                                }`}
                            >
                                <p className="text-sm font-semibold">Client</p>
                                <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                                    Passer des commandes et acceder aux creations.
                                </p>
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, role: "creator" })}
                                ref={creatorRef}
                                id="creator"
                                className={`rounded-lg border p-4 text-left transition ${
                                    formData.role === "creator"
                                        ? (isDark ? 'border-rose-500 bg-rose-500/10' : 'border-rose-500 bg-rose-50')
                                        : (isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-300')
                                }`}
                            >
                                <p className="text-sm font-semibold">Createur</p>
                                <p className={`text-xs mt-1 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                                    Vendre vos creations et gerer votre boutique.
                                </p>
                            </button>
                        </div>
                    </div>
                    {formData.role === "creator" && (
                        <>
                        <div className={`p-4 rounded-lg text-sm ${isDark ? 'bg-white/5 border border-white/10 text-white/80' : 'bg-slate-50 border border-gray-200 text-gray-700'}`}>
                            <p className="font-semibold mb-2">Infos créateur</p>
                            <ul className="space-y-1">
                                <li>Reversement : <span className="font-semibold">95% pour le créateur, 5% pour Krayne Agency</span>.</li>
                                <li>Après inscription, vous êtes redirigé vers Stripe Connect pour activer les reversements.</li>
                                <li>Vérification manuelle sous 24h pour garantir la qualité.</li>
                                <li>SIRET valide obligatoire (auto-entrepreneur ou société).</li>
                                <li>Inscription réservée aux professionnels disposant d’un SIRET.</li>
                                <li>Le créateur déclare ses revenus sur ses 95% et la plateforme sur ses 5%.</li>
                            </ul>
                        </div>
                        <input
                            type="text"
                            name="siret"
                            placeholder="Numéro de SIRET (14 chiffres)"
                            value={formData.siret}
                            onChange={handleChange}
                            required
                            maxLength={14}
                            minLength={14}
                            pattern="[0-9]{14}"
                            className={`w-full p-2 border rounded tracking-widest ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                        />
                        <input
                            type="email"
                            name="stripeEmail"
                            placeholder="Email Stripe (pour recevoir les paiements)"
                            value={formData.stripeEmail}
                            onChange={handleChange}
                            required
                            className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                        />
                        <input
                            type="text"
                            name="bankAccountName"
                            placeholder="Nom du compte bancaire"
                            value={formData.bankAccountName}
                            onChange={handleChange}
                            required
                            className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                        />
                        <input
                            type="text"
                            name="fullAddress"
                            placeholder="Adresse complète (pour la facturation Stripe)"
                            value={formData.fullAddress}
                            onChange={handleChange}
                            required
                            className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                        />
                        <textarea
                            name="motivation"
                            placeholder="Expliquez votre motivation et votre expérience (facultatif)"
                            value={formData.motivation}
                            onChange={handleChange}
                            className={`w-full p-2 border rounded min-h-[80px] ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                        />
                        </>
                    )}
                    <input
                        type="text"
                        name="nickname"
                        placeholder="Pseudo"
                        value={formData.nickname}
                        onChange={handleChange}
                        required
                        className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                    />
                    <input
                        type="text"
                        name="firstname"
                        placeholder="Prénom"
                        value={formData.firstname}
                        onChange={handleChange}
                        required
                        className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                    />
                    <input
                        type="text"
                        name="lastname"
                        placeholder="Nom"
                        value={formData.lastname}
                        onChange={handleChange}
                        required
                        className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                    />
                    <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                    />
                    <input
                        type="password"
                        name="password"
                        placeholder="Mot de passe (min 8 caractères)"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                    />
                    <input
                        type="password"
                        name="confirmPassword"
                        placeholder="Confirmez le mot de passe"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        className={`w-full p-2 border rounded ${isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white/95 border-gray-300 text-gray-800'}`}
                    />
                    <button
                        type="submit"
                        className="w-full p-3 rounded-lg text-white bg-rose-600 hover:bg-rose-700 transition duration-300"
                    >
                        S'inscrire
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Register;
